import React, { useMemo } from "react";
import { MATERIALS, DEFAULT_H } from "../../constants";
import { deriveSection } from "../../engine/sectionEngine";
import { LINE, DIM_STYLE, MAT_HATCH } from "../../engine/drawingStandards";
import {
  HatchDefs, WallHatch, DimLine, LevelAnnotation, ScaleBar, TitleBlock,
  mmToPx, worldToPx,
} from "./DrawingUtils";

const GROUND_GREY = "#e8e0d0";

export default function SectionDrawing({
  st,
  cut,               // { x1, y1, x2, y2 } in world mm
  lookDir = "left",
  scale   = 100,
  sheetSize = { w: 420, h: 297 },
  drawingNo = "PC-SC-01",
  sectionLabel = "A-A",
}) {
  const section = useMemo(() => deriveSection(st, cut, lookDir), [st, cut, lookDir]);
  const project = st.project || {};

  const sheetW = mmToPx(sheetSize.w);
  const sheetH = mmToPx(sheetSize.h);
  const margin = mmToPx(15);
  const titleH = mmToPx(30);
  const dimSp  = mmToPx(20);
  const levelW = mmToPx(30);  // width reserved for level annotations (right side)

  const drawX = margin + dimSp;
  const drawW = sheetW - margin - dimSp - levelW - margin;
  const drawH = sheetH - titleH - margin*2 - dimSp;

  if (!section) return (
    <svg width={sheetW} height={sheetH} style={{ background: "#fff" }}>
      <HatchDefs scale={scale}/>
      <rect width={sheetW} height={sheetH} fill="#fff" stroke="#000" strokeWidth={mmToPx(0.5)}/>
      <text x={sheetW/2} y={sheetH/2} textAnchor="middle" fontSize={mmToPx(4)} fontFamily={DIM_STYLE.font} fill="#aaa">
        No section cut defined — use the Section tool to place a cut line
      </text>
    </svg>
  );

  const { sectionWalls, visibleWalls, annotations, cutLen, maxH,
          floorSlabT, roofSlabT, plinthH, foundY } = section;

  // Scale the section to fit drawing area
  const sectionSpan  = Math.max(cutLen + 2000, 3000);
  const totalHeight  = maxH + roofSlabT + plinthH + 600 + 500; // top + bottom margins
  const scaleX = drawW / worldToPx(sectionSpan, scale);
  const scaleY = (drawH * 0.7) / worldToPx(totalHeight, scale);
  const sectionScale = Math.min(scaleX, scaleY, 1.0);

  // Section coordinate helpers
  const sx  = (worldMm) => drawX + dimSp + worldToPx(worldMm + 500, scale) * sectionScale;
  const sy  = (heightMm) => margin + dimSp + drawH * 0.72 - worldToPx(heightMm, scale) * sectionScale;

  const floorY   = sy(0);
  const foundYpx = sy(foundY);
  const roofYpx  = sy(maxH + roofSlabT);
  const sw       = mmToPx(LINE.OUTLINE.weight);
  const swV      = mmToPx(LINE.VISIBLE.weight);
  const swH      = mmToPx(LINE.HIDDEN.weight);

  return (
    <svg
      width={sheetW} height={sheetH}
      viewBox={`0 0 ${sheetW} ${sheetH}`}
      style={{ background: "#fff", display: "block" }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <HatchDefs scale={scale}/>

      {/* Sheet border */}
      <rect x={mmToPx(5)} y={mmToPx(5)} width={sheetW-mmToPx(10)} height={sheetH-mmToPx(10)}
        fill="none" stroke="#000" strokeWidth={mmToPx(0.5)}/>
      <rect x={mmToPx(10)} y={mmToPx(10)} width={sheetW-mmToPx(20)} height={sheetH-mmToPx(20)}
        fill="none" stroke="#000" strokeWidth={mmToPx(0.18)}/>

      {/* ── Ground below foundation ── */}
      <rect x={drawX} y={sy(-plinthH)} width={drawW} height={Math.abs(foundYpx - sy(-plinthH)) + mmToPx(20)}
        fill={`url(#hatch-EARTH)`} opacity={0.4}/>
      <rect x={drawX} y={sy(-plinthH)} width={drawW} height={Math.abs(foundYpx - sy(-plinthH)) + mmToPx(20)}
        fill={GROUND_GREY} opacity={0.15}/>

      {/* ── Plinth / foundation ── */}
      {sectionWalls.length > 0 && (
        <rect
          x={sx(Math.min(...sectionWalls.map(w => w.left)) - 300)}
          y={sy(-plinthH)}
          width={worldToPx(Math.max(...sectionWalls.map(w => w.right)) - Math.min(...sectionWalls.map(w => w.left)) + 600, scale) * sectionScale}
          height={worldToPx(plinthH, scale) * sectionScale}
          fill="white" stroke="#000" strokeWidth={sw}
        />
      )}
      {/* Plinth hatch */}
      {sectionWalls.length > 0 && (
        <rect
          x={sx(Math.min(...sectionWalls.map(w => w.left)) - 300)}
          y={sy(-plinthH)}
          width={worldToPx(Math.max(...sectionWalls.map(w => w.right)) - Math.min(...sectionWalls.map(w => w.left)) + 600, scale) * sectionScale}
          height={worldToPx(plinthH, scale) * sectionScale}
          fill={`url(#hatch-CONCRETE)`} opacity={0.5}
        />
      )}

      {/* ── Floor slab ── */}
      {sectionWalls.length > 0 && (
        <>
          <rect
            x={sx(Math.min(...sectionWalls.map(w => w.left)) - 100)}
            y={sy(0)}
            width={worldToPx(Math.max(...sectionWalls.map(w => w.right)) - Math.min(...sectionWalls.map(w => w.left)) + 200, scale) * sectionScale}
            height={worldToPx(floorSlabT, scale) * sectionScale}
            fill="white" stroke="#000" strokeWidth={sw}
          />
          <rect
            x={sx(Math.min(...sectionWalls.map(w => w.left)) - 100)}
            y={sy(0)}
            width={worldToPx(Math.max(...sectionWalls.map(w => w.right)) - Math.min(...sectionWalls.map(w => w.left)) + 200, scale) * sectionScale}
            height={worldToPx(floorSlabT, scale) * sectionScale}
            fill={`url(#hatch-CONCRETE)`} opacity={0.7}
          />
        </>
      )}

      {/* ── Roof slab ── */}
      {sectionWalls.length > 0 && (
        <>
          <rect
            x={sx(Math.min(...sectionWalls.map(w => w.left)) - 100)}
            y={roofYpx}
            width={worldToPx(Math.max(...sectionWalls.map(w => w.right)) - Math.min(...sectionWalls.map(w => w.left)) + 200, scale) * sectionScale}
            height={worldToPx(roofSlabT, scale) * sectionScale}
            fill="white" stroke="#000" strokeWidth={sw}
          />
          <rect
            x={sx(Math.min(...sectionWalls.map(w => w.left)) - 100)}
            y={roofYpx}
            width={worldToPx(Math.max(...sectionWalls.map(w => w.right)) - Math.min(...sectionWalls.map(w => w.left)) + 200, scale) * sectionScale}
            height={worldToPx(roofSlabT, scale) * sectionScale}
            fill={`url(#hatch-CONCRETE)`} opacity={0.7}
          />
        </>
      )}

      {/* ── Cut walls ── */}
      {sectionWalls.map(wall => {
        const wx  = sx(wall.left);
        const ww  = worldToPx(wall.wallT, scale) * sectionScale;
        const wh  = worldToPx(wall.wallH, scale) * sectionScale;
        const wy  = sy(wall.wallH);
        const matId = wall.matId;

        return (
          <g key={wall.id}>
            {/* Wall body */}
            <WallHatch
              x={wx} y={wy}
              w={ww} h={wh}
              matId={matId}
              isSection={true}
            />
            {/* Opening voids */}
            {wall.openings.map((op, i) => {
              const opY  = sy(op.top);
              const opH  = worldToPx(op.top - op.base, scale) * sectionScale;
              return (
                <g key={i}>
                  <rect x={wx} y={opY} width={ww} height={opH} fill="white" stroke="none"/>
                  {/* Window sill line */}
                  {op.type === "window" && (
                    <line x1={wx} y1={sy(op.base)} x2={wx+ww} y2={sy(op.base)}
                      stroke="#000" strokeWidth={swV}/>
                  )}
                  {/* Opening outline */}
                  <rect x={wx} y={opY} width={ww} height={opH}
                    fill="none" stroke="#000" strokeWidth={swV}/>
                </g>
              );
            })}
          </g>
        );
      })}

      {/* ── Visible walls beyond cut (dashed) ── */}
      {visibleWalls.map(wall => {
        const x1 = sx(wall.xA), x2 = sx(wall.xB);
        const y1 = sy(wall.height), y2 = floorY;
        const opacity = Math.max(0.2, 1 - wall.dist / 6000);
        return (
          <g key={wall.id} opacity={opacity}>
            <line x1={x1} y1={y1} x2={x1} y2={y2}
              stroke="#000" strokeWidth={swH}
              strokeDasharray={`${mmToPx(4)} ${mmToPx(3)}`}/>
            <line x1={x2} y1={y1} x2={x2} y2={y2}
              stroke="#000" strokeWidth={swH}
              strokeDasharray={`${mmToPx(4)} ${mmToPx(3)}`}/>
            <line x1={x1} y1={y1} x2={x2} y2={y1}
              stroke="#000" strokeWidth={swH}
              strokeDasharray={`${mmToPx(4)} ${mmToPx(3)}`}/>
          </g>
        );
      })}

      {/* ── Ground line ── */}
      <line x1={drawX} y1={sy(-plinthH)} x2={drawX + drawW} y2={sy(-plinthH)}
        stroke="#000" strokeWidth={sw}/>

      {/* ── FFL (Finished Floor Level) line ── */}
      <line x1={drawX} y1={floorY} x2={drawX + drawW} y2={floorY}
        stroke="#000" strokeWidth={swV} strokeDasharray={`${mmToPx(8)} ${mmToPx(2)} ${mmToPx(2)} ${mmToPx(2)}`}/>

      {/* ── Level annotations ── */}
      {annotations.map((ann, i) => (
        <LevelAnnotation key={i}
          x={drawX + drawW + mmToPx(2)}
          y={sy(ann.y)}
          label={ann.label}
          side="right"
        />
      ))}

      {/* ── Height dimension ── */}
      {sectionWalls.length > 0 && (
        <DimLine
          x1={sx(sectionWalls[0].left) - mmToPx(5)}
          y1={floorY}
          x2={sx(sectionWalls[0].left) - mmToPx(5)}
          y2={sy(maxH)}
          offset={15}
          text={`${(maxH/1000).toFixed(2)}m`}
          orientation="v"
        />
      )}

      {/* ── Section label ── */}
      <text x={drawX + drawW/2} y={margin + mmToPx(6)}
        textAnchor="middle" fontSize={mmToPx(5)} fontWeight="700"
        fontFamily={DIM_STYLE.font} fill="#000">
        SECTION {sectionLabel}
      </text>

      {/* ── Scale bar ── */}
      <ScaleBar
        x={margin + mmToPx(5)}
        y={sheetH - titleH - mmToPx(12)}
        scale={scale}
        segments={3}
      />

      {/* ── Title block ── */}
      <TitleBlock
        x={mmToPx(10)} y={sheetH - titleH + mmToPx(2)}
        w={sheetW - mmToPx(20)} h={titleH - mmToPx(4)}
        project={project}
        drawingTitle={`SECTION ${sectionLabel}`}
        drawingNo={drawingNo}
        scale={scale}
        date={project.date}
      />
    </svg>
  );
}
